import { useMemo, useState } from 'react';
import {
  RefreshCcw, QrCode, RotateCcw, Wifi, WifiOff, Users, PlugZap,
  Settings, FolderKanban, X, Plus, Tag, MapPin, CheckCircle,
  CheckCircle2, Trash2, Save
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/Table';
import {
  useAtualizarEvolutionInstance,
  useConectarEvolutionInstance,
  useEvolutionInstanceOverview,
  useQrCodeEvolutionInstance,
  useReiniciarEvolutionInstance,
  useGruposDB,
  useCreateGrupoDB,
  useUpdateGrupoDB,
  useCategoriasSistema,
  useGrupoCategorias,
  useAddGrupoCategoria,
  useRemoveGrupoCategoria,
  type GrupoDB,
  type EvolutionGrupo,
  type CategoriaSistema,
  type GrupoCategoria
} from '@/hooks/useGrupos';

const statusMap: Record<string, { label: string; variant: 'success' | 'warning' | 'danger' | 'default' }> = {
  online: { label: 'Conectada', variant: 'success' },
  connecting: { label: 'Conectando', variant: 'warning' },
  offline: { label: 'Desconectada', variant: 'danger' },
  open: { label: 'Conectada', variant: 'success' },
  close: { label: 'Desconectada', variant: 'danger' },
  unknown: { label: 'Indefinido', variant: 'default' }
};

const toQrImageSrc = (value?: string | null) => {
  if (!value) return null;
  if (value.startsWith('data:image')) return value;
  return `data:image/png;base64,${value}`;
};

type TabType = 'instancia' | 'grupos';

// Tag input component for arrays
function TagInput({
  label,
  values,
  onChange,
  placeholder,
  helperText
}: {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  helperText?: string;
}) {
  const [input, setInput] = useState('');

  const addTag = () => {
    const trimmed = input.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
      setInput('');
    }
  };

  const removeTag = (tag: string) => {
    onChange(values.filter((v) => v !== tag));
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-ds-text-secondary mb-2">{label}</label>
      <div className="flex flex-wrap gap-2 mb-2">
        {values.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-ds-success/10 text-ds-success border border-ds-success/20"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="hover:text-ds-danger transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder={placeholder || 'Digite e pressione Enter'}
          className="flex-1"
        />
        <Button type="button" variant="secondary" size="sm" onClick={addTag}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {helperText && <p className="mt-1.5 text-xs text-ds-text-secondary">{helperText}</p>}
    </div>
  );
}

// Configuration Modal
type ConfigModalMode = 'create' | 'edit';

function ConfigModal({
  grupo,
  evolutionGrupo,
  isOpen,
  onClose,
  mode
}: {
  grupo: GrupoDB | null;
  evolutionGrupo: EvolutionGrupo | null;
  isOpen: boolean;
  onClose: () => void;
  mode: ConfigModalMode;
}) {
  const [activeSection, setActiveSection] = useState<'geral' | 'categorias'>('geral');
  const createMutation = useCreateGrupoDB();
  const updateMutation = useUpdateGrupoDB();
  const { data: categoriasData } = useCategoriasSistema();
  const { data: grupoCategoriasData } = useGrupoCategorias(grupo?.id || null);
  const addCategoria = useAddGrupoCategoria();
  const removeCategoria = useRemoveGrupoCategoria();

  const [form, setForm] = useState<Partial<GrupoDB>>({});

  const isCreate = mode === 'create';
  const isLoading = createMutation.isPending || updateMutation.isPending;

  // Reset form when modal opens
  useState(() => {
    if (isCreate && evolutionGrupo) {
      setForm({
        nome: evolutionGrupo.nome,
        descricao: evolutionGrupo.descricao || '',
        uf: [],
        palavras_chave: [],
        palavras_chave_exclusao: [],
        nichos: [],
        cnaes: [],
        recebe_licitacoes: true,
        recebe_noticias: true,
        correlacao_automatica: true,
        score_minimo: 50
      });
    } else if (grupo) {
      setForm({
        nome: grupo.nome,
        descricao: grupo.descricao,
        uf: grupo.uf || [],
        palavras_chave: grupo.palavras_chave || [],
        palavras_chave_exclusao: grupo.palavras_chave_exclusao || [],
        nichos: grupo.nichos || [],
        cnaes: grupo.cnaes || [],
        recebe_licitacoes: grupo.recebe_licitacoes,
        recebe_noticias: grupo.recebe_noticias,
        correlacao_automatica: grupo.correlacao_automatica,
        score_minimo: grupo.score_minimo
      });
    }
  });

  if (!isOpen) return null;
  if (!isCreate && !grupo) return null;
  if (isCreate && !evolutionGrupo) return null;

  const categoriasSistema = categoriasData?.categorias || [];
  const grupoCategorias = grupoCategoriasData?.categorias || [];
  const grupoCategoriaIds = new Set(grupoCategorias.map((c: GrupoCategoria) => c.id));

  const handleSave = async () => {
    if (isCreate) {
      await createMutation.mutateAsync(form);
    } else if (grupo) {
      await updateMutation.mutateAsync({ id: grupo.id, data: form });
    }
    onClose();
  };

  const updateField = <K extends keyof GrupoDB>(field: K, value: GrupoDB[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-ds-surface border border-ds-border rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-ds-border">
          <div>
            <h3 className="text-lg font-semibold text-ds-text-primary">
              {isCreate ? 'Cadastrar Grupo' : 'Configurar Grupo'}
            </h3>
            <p className="text-sm text-ds-text-secondary mt-0.5">
              {isCreate ? evolutionGrupo?.nome : grupo?.nome}
            </p>
          </div>
          <button onClick={onClose} className="text-ds-text-secondary hover:text-ds-text-primary transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-ds-border">
          <button
            onClick={() => setActiveSection('geral')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'geral'
                ? 'border-ds-success text-ds-success'
                : 'border-transparent text-ds-text-secondary hover:text-ds-text-primary'
            }`}
          >
            <Settings className="h-4 w-4 inline mr-1.5" />
            Configurações Gerais
          </button>
          <button
            onClick={() => setActiveSection('categorias')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'categorias'
                ? 'border-ds-success text-ds-success'
                : 'border-transparent text-ds-text-secondary hover:text-ds-text-primary'
            }`}
          >
            <Tag className="h-4 w-4 inline mr-1.5" />
            Categorias
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {activeSection === 'geral' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ds-text-secondary mb-2">Nome</label>
                  <Input
                    value={form.nome || ''}
                    onChange={(e) => updateField('nome', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ds-text-secondary mb-2">Descrição / Segmento</label>
                  <Input
                    value={form.descricao || ''}
                    onChange={(e) => updateField('descricao', e.target.value)}
                  />
                </div>
              </div>

              <TagInput
                label="Nichos de Atuação"
                values={form.nichos || []}
                onChange={(v) => updateField('nichos', v)}
                placeholder="Ex: Infraestrutura, Saneamento, TI"
                helperText="Áreas de atuação do grupo para matching inteligente"
              />

              <TagInput
                label="Palavras-chave de Inclusão"
                values={form.palavras_chave || []}
                onChange={(v) => updateField('palavras_chave', v)}
                placeholder="Ex: rodovia, pavimentação, asfalto"
                helperText="Palavras que devem estar presentes para incluir o conteúdo"
              />

              <TagInput
                label="Palavras-chave de Exclusão"
                values={form.palavras_chave_exclusao || []}
                onChange={(v) => updateField('palavras_chave_exclusao', v)}
                placeholder="Ex: manutenção, limpeza"
                helperText="Palavras que descartam o conteúdo automaticamente"
              />

              <TagInput
                label="UFs de Interesse"
                values={form.uf || []}
                onChange={(v) => updateField('uf', v)}
                placeholder="Ex: SP, RJ, MG"
                helperText="Estados de interesse para licitações e notícias"
              />

              <TagInput
                label="CNAEs Relevantes"
                values={form.cnaes || []}
                onChange={(v) => updateField('cnaes', v)}
                placeholder="Ex: 4110700, 4291000"
                helperText="Códigos CNAE relacionados ao grupo (opcional)"
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-lg border border-ds-border bg-ds-surface2">
                  <input
                    type="checkbox"
                    id="recebe_licitacoes"
                    checked={form.recebe_licitacoes !== false}
                    onChange={(e) => updateField('recebe_licitacoes', e.target.checked)}
                    className="h-4 w-4 rounded border-ds-border text-ds-success focus:ring-ds-success"
                  />
                  <label htmlFor="recebe_licitacoes" className="text-sm text-ds-text-primary cursor-pointer">
                    Recebe Licitações
                  </label>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border border-ds-border bg-ds-surface2">
                  <input
                    type="checkbox"
                    id="recebe_noticias"
                    checked={form.recebe_noticias !== false}
                    onChange={(e) => updateField('recebe_noticias', e.target.checked)}
                    className="h-4 w-4 rounded border-ds-border text-ds-success focus:ring-ds-success"
                  />
                  <label htmlFor="recebe_noticias" className="text-sm text-ds-text-primary cursor-pointer">
                    Recebe Notícias
                  </label>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border border-ds-border bg-ds-surface2">
                  <input
                    type="checkbox"
                    id="correlacao_automatica"
                    checked={form.correlacao_automatica !== false}
                    onChange={(e) => updateField('correlacao_automatica', e.target.checked)}
                    className="h-4 w-4 rounded border-ds-border text-ds-success focus:ring-ds-success"
                  />
                  <label htmlFor="correlacao_automatica" className="text-sm text-ds-text-primary cursor-pointer">
                    Correlação Automática
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-ds-text-secondary mb-2">
                  Score Mínimo para Match ({form.score_minimo || 50})
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={form.score_minimo || 50}
                  onChange={(e) => updateField('score_minimo', parseInt(e.target.value, 10))}
                  className="w-full accent-ds-success"
                />
                <div className="flex justify-between text-xs text-ds-text-secondary mt-1">
                  <span>0 (tudo passa)</span>
                  <span>50 (padrão)</span>
                  <span>100 (match perfeito)</span>
                </div>
              </div>
            </>
          )}

          {activeSection === 'categorias' && (
            <div className="space-y-4">
              <p className="text-sm text-ds-text-secondary">
                Selecione as categorias do sistema que se aplicam a este grupo. Cada categoria contribui para o score de matching.
              </p>

              <div className="space-y-2">
                {categoriasSistema.map((cat: CategoriaSistema) => {
                  const isSelected = grupoCategoriaIds.has(cat.id);
                  const grupoCat = grupoCategorias.find((c) => c.id === cat.id);

                  return (
                    <div
                      key={cat.id}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        isSelected
                          ? 'border-ds-success/50 bg-ds-success/5'
                          : 'border-ds-border bg-ds-surface2 hover:border-ds-border/80'
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-ds-text-primary">{cat.nome}</span>
                          <Badge variant={cat.tipo === 'licitacao' ? 'info' : cat.tipo === 'noticia' ? 'warning' : 'default'} size="sm">
                            {cat.tipo}
                          </Badge>
                        </div>
                        {cat.descricao && (
                          <p className="text-xs text-ds-text-secondary mt-0.5">{cat.descricao}</p>
                        )}
                      </div>

                      {isSelected ? (
                        <div className="flex items-center gap-2">
                          {grupoCat && (
                            <span className="text-xs text-ds-text-secondary">Peso: {grupoCat.peso}</span>
                          )}
                          <CheckCircle className="h-4 w-4 text-ds-success" />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => grupo && removeCategoria.mutate({ grupoId: grupo.id, categoriaId: cat.id })}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-ds-danger" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => grupo && addCategoria.mutate({ grupoId: grupo.id, categoriaId: cat.id })}
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" />
                          Adicionar
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-ds-border">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            isLoading={isLoading}
          >
            <Save className="h-4 w-4 mr-2" />
            {isCreate ? 'Cadastrar Grupo' : 'Salvar Configurações'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Grupos() {
  const [activeTab, setActiveTab] = useState<TabType>('instancia');
  const [qrData, setQrData] = useState<string | null>(null);
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [configModalGrupo, setConfigModalGrupo] = useState<GrupoDB | null>(null);
  const [createModalGrupo, setCreateModalGrupo] = useState<EvolutionGrupo | null>(null);

  const { data, isLoading, isError, error } = useEvolutionInstanceOverview();
  const atualizarMutation = useAtualizarEvolutionInstance();
  const conectarMutation = useConectarEvolutionInstance();
  const reiniciarMutation = useReiniciarEvolutionInstance();
  const qrMutation = useQrCodeEvolutionInstance();

  const { data: gruposDBData, isLoading: gruposDBLoading } = useGruposDB();

  const instance = data?.instance;
  const grupos = data?.grupos || [];

  const statusInfo = statusMap[(instance?.status || '').toLowerCase()] || statusMap.unknown;

  // Map Evolution groups to DB groups by name
  const gruposDBMap = useMemo(() => {
    const map = new Map<string, GrupoDB>();
    gruposDBData?.grupos?.forEach((g: GrupoDB) => {
      map.set(g.nome.toLowerCase().trim(), g);
    });
    return map;
  }, [gruposDBData]);

  const errorMessage = (error as { message?: string } | null)?.message || 'Falha ao carregar dados da instância Evolution.';

  const handleAtualizar = async () => {
    const result = await atualizarMutation.mutateAsync();
    setFeedbackMessage(result.message || 'Dados da instância atualizados.');
  };

  const handleConectar = async () => {
    const result = await conectarMutation.mutateAsync();
    setFeedbackMessage(result.message || 'Solicitação de conexão enviada.');
    if (result.qr) setQrData(result.qr);
    if (result.pairingCode) setPairingCode(result.pairingCode);
  };

  const handleReiniciar = async () => {
    const result = await reiniciarMutation.mutateAsync();
    setFeedbackMessage(result.message || 'Instância reiniciada.');
  };

  const handleQrCode = async () => {
    const result = await qrMutation.mutateAsync();
    setFeedbackMessage(result.message || 'QRCode atualizado.');
    setQrData(result.qr || null);
    setPairingCode(result.pairingCode || null);
  };

  const isActionLoading = atualizarMutation.isPending || conectarMutation.isPending || reiniciarMutation.isPending || qrMutation.isPending;
  const actionError = (atualizarMutation.error || conectarMutation.error || reiniciarMutation.error || qrMutation.error) as { message?: string } | null;
  const actionErrorMessage = actionError?.message || null;
  const qrImageSrc = toQrImageSrc(qrData);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ds-text-primary">Grupos</h1>
          <p className="text-ds-text-secondary mt-1">
            Gestão de grupos de notificação e configuração de correlação inteligente
          </p>
        </div>
        <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-ds-border">
        <button
          onClick={() => setActiveTab('instancia')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'instancia'
              ? 'border-ds-success text-ds-success'
              : 'border-transparent text-ds-text-secondary hover:text-ds-text-primary'
          }`}
        >
          <Wifi className="h-4 w-4" />
          Instância Evolution
        </button>
        <button
          onClick={() => setActiveTab('grupos')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'grupos'
              ? 'border-ds-success text-ds-success'
              : 'border-transparent text-ds-text-secondary hover:text-ds-text-primary'
          }`}
        >
          <FolderKanban className="h-4 w-4" />
          Grupos Cadastrados
        </button>
      </div>

      {/* Tab: Instância Evolution */}
      {activeTab === 'instancia' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Gestão da Instância</CardTitle>
              <CardDescription>
                Acompanhe o status e execute ações operacionais sem sair da plataforma.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isError && <p className="text-sm text-ds-danger">{errorMessage}</p>}

              {!isError && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg border border-ds-border bg-ds-surface2">
                    <p className="text-sm text-ds-text-secondary">Instância</p>
                    <p className="text-base font-semibold text-ds-text-primary mt-1">{instance?.name || '-'}</p>
                    <p className="text-xs text-ds-text-secondary mt-1">Ref: {instance?.reference || '-'}</p>
                  </div>
                  <div className="p-4 rounded-lg border border-ds-border bg-ds-surface2">
                    <p className="text-sm text-ds-text-secondary">Perfil</p>
                    <p className="text-base font-semibold text-ds-text-primary mt-1">{instance?.profile_name || 'Sem perfil'}</p>
                    <p className="text-xs text-ds-text-secondary mt-1">Owner: {instance?.owner_jid || 'N/A'}</p>
                  </div>
                  <div className="p-4 rounded-lg border border-ds-border bg-ds-surface2">
                    <p className="text-sm text-ds-text-secondary">Total de Grupos</p>
                    <p className="text-base font-semibold text-ds-text-primary mt-1">{grupos.length}</p>
                    <p className="text-xs text-ds-text-secondary mt-1">Último status: {instance?.raw_status || '-'}</p>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={handleAtualizar} isLoading={atualizarMutation.isPending} className="min-h-[44px]">
                  <RefreshCcw className="h-4 w-4 mr-2" />
                  Atualizar
                </Button>
                <Button type="button" variant="secondary" onClick={handleConectar} isLoading={conectarMutation.isPending} className="min-h-[44px]">
                  <PlugZap className="h-4 w-4 mr-2" />
                  Conectar
                </Button>
                <Button type="button" variant="secondary" onClick={handleQrCode} isLoading={qrMutation.isPending} className="min-h-[44px]">
                  <QrCode className="h-4 w-4 mr-2" />
                  Gerar QR Code
                </Button>
                <Button type="button" variant="ghost" onClick={handleReiniciar} isLoading={reiniciarMutation.isPending} className="min-h-[44px]">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reiniciar Instância
                </Button>
              </div>

              {feedbackMessage && <p className="text-sm text-ds-success">{feedbackMessage}</p>}
              {actionErrorMessage && <p className="text-sm text-ds-danger">{actionErrorMessage}</p>}

              {(qrImageSrc || pairingCode) && (
                <div className="rounded-lg border border-ds-border p-4 bg-ds-surface2">
                  <p className="text-sm font-medium text-ds-text-primary mb-3">Dados de Conexão</p>
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    {qrImageSrc && (
                      <img
                        src={qrImageSrc}
                        alt="QR Code da instância Evolution"
                        className="w-44 h-44 rounded-lg border border-ds-border bg-white p-2"
                      />
                    )}
                    <div className="space-y-2">
                      {pairingCode && (
                        <p className="text-sm text-ds-text-primary">
                          Pairing code: <span className="font-semibold">{pairingCode}</span>
                        </p>
                      )}
                      {!qrImageSrc && !pairingCode && (
                        <p className="text-sm text-ds-text-secondary">
                          A instância está conectada e não retornou QR code no momento.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {!isLoading && grupos.map((grupo) => {
              const dbGrupo = gruposDBMap.get(grupo.nome.toLowerCase().trim());
              const isCadastrado = !!dbGrupo;

              return (
                <Card
                  key={grupo.id || grupo.nome}
                  variant="hover"
                  className="cursor-pointer relative"
                  onClick={() => {
                    if (isCadastrado) {
                      setConfigModalGrupo(dbGrupo);
                    } else {
                      setCreateModalGrupo(grupo);
                    }
                  }}
                >
                  {isCadastrado && (
                    <div className="absolute top-3 right-3">
                      <Badge variant="success" size="sm">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Cadastrado
                      </Badge>
                    </div>
                  )}
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-ds-info/10">
                          <Users className="h-5 w-5 text-ds-info" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{grupo.nome}</CardTitle>
                          <CardDescription>{grupo.total_participantes} participantes</CardDescription>
                        </div>
                      </div>
                      {!isCadastrado && <Badge variant="default">Grupo</Badge>}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-ds-text-secondary mb-4">
                      {grupo.descricao || 'Sem descricao'}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-ds-text-secondary text-sm">
                        {instance?.status === 'online' ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
                        {instance?.status === 'online' ? 'Instância online' : 'Instância em reconexão'}
                      </div>
                      {isCadastrado ? (
                        <Badge variant="default" size="sm">
                          <Settings className="h-3 w-3 mr-1" />
                          Configurar
                        </Badge>
                      ) : (
                        <Badge variant="warning" size="sm">
                          <Plus className="h-3 w-3 mr-1" />
                          Cadastrar
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {isLoading && (
              <Card className="md:col-span-2 lg:col-span-3">
                <CardContent className="py-8 text-center text-ds-text-secondary">
                  Carregando grupos e status da instância...
                </CardContent>
              </Card>
            )}

            {!isLoading && grupos.length === 0 && !isError && (
              <Card className="md:col-span-2 lg:col-span-3">
                <CardContent className="py-8 text-center text-ds-text-secondary">
                  Nenhum grupo retornado para essa instância no momento.
                </CardContent>
              </Card>
            )}
          </div>


        </div>
      )}

      {/* Tab: Grupos Cadastrados */}
      {activeTab === 'grupos' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Grupos Cadastrados no Sistema</CardTitle>
                  <CardDescription>
                    Configure nichos, palavras-chave, UFs e categorias para correlação inteligente de conteúdo.
                  </CardDescription>
                </div>
                <Badge variant="info" size="sm">
                  {gruposDBData?.pagination.total || 0} grupos
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {gruposDBLoading ? (
                <p className="text-center text-ds-text-secondary py-8">Carregando grupos...</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Segmento</TableHead>
                      <TableHead>UFs</TableHead>
                      <TableHead>Nichos</TableHead>
                      <TableHead>Palavras-chave</TableHead>
                      <TableHead>Configurações</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {gruposDBData?.grupos.map((grupo: GrupoDB) => (
                      <TableRow key={grupo.id}>
                        <TableCell>
                          <div className="font-medium text-ds-text-primary">{grupo.nome}</div>
                          <div className="text-xs text-ds-text-secondary">{grupo.total_membros} membros</div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-ds-text-secondary">
                            {grupo.descricao || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(grupo.uf || []).slice(0, 3).map((uf: string) => (
                              <Badge key={uf} variant="info" size="sm">
                                <MapPin className="h-3 w-3 mr-0.5" />
                                {uf}
                              </Badge>
                            ))}
                            {(grupo.uf || []).length > 3 && (
                              <Badge variant="default" size="sm">+{(grupo.uf || []).length - 3}</Badge>
                            )}
                            {(grupo.uf || []).length === 0 && (
                              <span className="text-xs text-ds-text-secondary">Todas</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(grupo.nichos || []).slice(0, 2).map((nicho: string) => (
                              <Badge key={nicho} variant="success" size="sm">{nicho}</Badge>
                            ))}
                            {(grupo.nichos || []).length > 2 && (
                              <Badge variant="default" size="sm">+{(grupo.nichos || []).length - 2}</Badge>
                            )}
                            {(grupo.nichos || []).length === 0 && (
                              <span className="text-xs text-ds-text-secondary">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(grupo.palavras_chave || []).slice(0, 2).map((kw: string) => (
                              <Badge key={kw} variant="warning" size="sm">{kw}</Badge>
                            ))}
                            {(grupo.palavras_chave || []).length > 2 && (
                              <Badge variant="default" size="sm">+{(grupo.palavras_chave || []).length - 2}</Badge>
                            )}
                            {(grupo.palavras_chave || []).length === 0 && (
                              <span className="text-xs text-ds-text-secondary">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {grupo.recebe_licitacoes && (
                              <Badge variant="success" size="sm">Licitações</Badge>
                            )}
                            {grupo.recebe_noticias && (
                              <Badge variant="info" size="sm">Notícias</Badge>
                            )}
                            {grupo.correlacao_automatica && (
                              <Badge variant="default" size="sm">Auto</Badge>
                            )}
                            {!grupo.recebe_licitacoes && !grupo.recebe_noticias && (
                              <span className="text-xs text-ds-text-secondary">Inativo</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfigModalGrupo(grupo)}
                          >
                            <Settings className="h-4 w-4 mr-1" />
                            Configurar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}

                    {(!gruposDBData?.grupos || gruposDBData.grupos.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-ds-text-secondary py-8">
                          Nenhum grupo cadastrado no sistema.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {isActionLoading && (
        <p className="text-xs text-ds-text-secondary">Executando ação na instância...</p>
      )}

      {/* Config Modal (edit existing) */}
      <ConfigModal
        grupo={configModalGrupo}
        evolutionGrupo={null}
        isOpen={!!configModalGrupo}
        onClose={() => setConfigModalGrupo(null)}
        mode="edit"
      />

      {/* Config Modal (create from Evolution) */}
      <ConfigModal
        grupo={null}
        evolutionGrupo={createModalGrupo}
        isOpen={!!createModalGrupo}
        onClose={() => setCreateModalGrupo(null)}
        mode="create"
      />
    </div>
  );
}

export { Grupos };
